import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from './ui/PageHeader';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import { AdminQueueState } from './ui/AdminQueueState';
import { useToast } from '../contexts/ToastContext';
import {
  adminGetPassportFlags,
  adminGetPassportHealth,
  adminListPassportEncounters,
  adminListPassportReviewQueue,
  adminPassportReviewDecision,
  adminSetPassportFlag,
  type PassportEncounter,
  type PassportFeatureFlags,
  type PassportReviewQueueItem,
} from '../services/api/passport';
import {
  SUBJECT_TYPE_LABELS,
  encounterStatusLabel,
  formatEncounterDateTime,
  riskStatusLabel,
  verificationLevelLabel,
} from './passport/passportCopy';
import { useAdminTabParam } from '../hooks/useAdminTabParam';

const PASSPORT_TABS = ['flags', 'queue', 'encounters', 'health'] as const;

const FLAG_LABELS: Record<keyof PassportFeatureFlags, string> = {
  passport_enabled: 'Bilt Passport (master switch)',
  vehicle_encounters_enabled: 'Vehicle encounters',
  event_encounters_enabled: 'Event encounters',
  passport_collections_enabled: 'Collections',
  shop_encounters_enabled: 'Shop encounters',
  club_encounters_enabled: 'Club encounters',
  sponsor_campaigns_enabled: 'Sponsor / organizer campaigns',
  passport_rewards_enabled: 'Rewards / claims',
  passport_location_verification_enabled: 'Location verification',
  passport_offline_scans_enabled: 'Offline scan queue',
  passport_public_profiles_enabled: 'Public profiles & memories',
};

function encounterSubjectLabel(enc: PassportEncounter): string {
  return enc.subject?.title || `${SUBJECT_TYPE_LABELS[enc.subject_type]} #${enc.subject_id}`;
}

/**
 * Bilt Passport moderation shell — flags, review queue, and encounter search.
 * Additive admin surface; every call degrades to an honest error/empty state
 * if a backend action isn't live yet (e.g. there is no admin appeals list
 * endpoint today, so that tab isn't shown until one ships).
 */
export const AdminPassport: React.FC = () => {
  const { showToast } = useToast();
  const [tab, setTab] = useAdminTabParam(PASSPORT_TABS, 'flags');
  const [flags, setFlags] = useState<PassportFeatureFlags | null>(null);
  const [flagBusy, setFlagBusy] = useState<string | null>(null);
  const [queue, setQueue] = useState<PassportReviewQueueItem[]>([]);
  const [encounters, setEncounters] = useState<PassportEncounter[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PassportReviewQueueItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [health, setHealth] = useState<Record<string, unknown> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      if (tab === 'flags') {
        setFlags(await adminGetPassportFlags());
      } else if (tab === 'queue') {
        setQueue(await adminListPassportReviewQueue('pending'));
      } else if (tab === 'health') {
        setHealth(await adminGetPassportHealth());
      } else {
        setEncounters(await adminListPassportEncounters(statusFilter ? { status: statusFilter } : {}));
      }
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : 'Bilt Passport backend is not reachable right now.');
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleFlag = async (key: keyof PassportFeatureFlags, enabled: boolean) => {
    setFlagBusy(key);
    try {
      await adminSetPassportFlag(key, enabled);
      setFlags((prev) => (prev ? { ...prev, [key]: enabled } : prev));
      showToast(`${FLAG_LABELS[key]} ${enabled ? 'enabled' : 'disabled'}.`, 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to update flag', 'error');
    } finally {
      setFlagBusy(null);
    }
  };

  const decide = async (item: PassportReviewQueueItem, decision: 'approved' | 'needs_more_info') => {
    setBusyId(item.id);
    try {
      await adminPassportReviewDecision({ encounter_id: item.id, decision });
      showToast(decision === 'approved' ? 'Encounter approved.' : 'Marked as needing more info.', 'success');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to record decision', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setBusyId(rejectTarget.id);
    try {
      await adminPassportReviewDecision({
        encounter_id: rejectTarget.id,
        decision: 'rejected',
        reason: rejectReason || undefined,
      });
      showToast('Encounter marked not verified.', 'success');
      setRejectTarget(null);
      setRejectReason('');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to reject encounter', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="Bilt Passport moderation"
        description="Control rollout flags and review flagged encounters. Not the gate/admission check-in scanner."
        actions={
          <Button type="button" variant="secondary" onClick={() => void load()}>
            Refresh
          </Button>
        }
      />

      <div role="tablist" aria-label="Passport moderation views" className="flex flex-wrap gap-1">
        {(PASSPORT_TABS).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium min-h-[44px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 ${
              tab === key
                ? 'bg-orange-500/15 text-orange-300 border border-orange-500/35'
                : 'text-gray-400 border border-transparent hover:bg-white/[0.04]'
            }`}
          >
            {key === 'flags'
              ? 'Rollout flags'
              : key === 'queue'
                ? 'Review queue'
                : key === 'health'
                  ? 'Health'
                  : 'All encounters'}
          </button>
        ))}
      </div>

      {tab === 'encounters' && (
        <div className="flex flex-wrap gap-2 items-center">
          <label className="sr-only" htmlFor="passport-status-filter">
            Filter by status
          </label>
          <select
            id="passport-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input max-w-[200px] min-h-[44px]"
          >
            <option value="">All statuses</option>
            <option value="pending">Pending validation</option>
            <option value="verified">Verified</option>
            <option value="rejected">Not verified</option>
          </select>
        </div>
      )}

      {loading ? (
        <p className="text-gray-400" role="status">
          Loading…
        </p>
      ) : loadError ? (
        <AdminQueueState variant="error" title="Could not load Passport data" description={loadError} onRetry={() => void load()} />
      ) : tab === 'flags' ? (
        <ul className="space-y-2 list-none max-w-2xl">
          {(Object.keys(FLAG_LABELS) as (keyof PassportFeatureFlags)[]).map((key) => {
            const enabled = Boolean(flags?.[key]);
            return (
              <li key={key} className="form-section flex items-center justify-between gap-4 !py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white">{FLAG_LABELS[key]}</p>
                  <p className="text-xs text-gray-500 font-mono">{key}</p>
                </div>
                <Button
                  type="button"
                  variant={enabled ? 'primary' : 'secondary'}
                  size="sm"
                  loading={flagBusy === key}
                  onClick={() => void toggleFlag(key, !enabled)}
                  aria-pressed={enabled}
                >
                  {enabled ? 'On' : 'Off'}
                </Button>
              </li>
            );
          })}
        </ul>
      ) : tab === 'queue' ? (
        queue.length === 0 ? (
          <AdminQueueState variant="empty" title="Review queue is clear" description="Encounters pending validation will appear here." />
        ) : (
          <ul className="space-y-4 list-none">
            {queue.map((item) => (
              <li key={item.id} className="form-section">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-white">{encounterSubjectLabel(item)}</h3>
                    <p className="text-sm text-gray-400 mt-1">{formatEncounterDateTime(item.occurred_at)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {verificationLevelLabel(item.verification_level)} · {riskStatusLabel(item.risk_status)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <Button type="button" variant="primary" size="sm" loading={busyId === item.id} onClick={() => void decide(item, 'approved')}>
                      Approve
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={busyId === item.id}
                      onClick={() => {
                        setRejectTarget(item);
                        setRejectReason('');
                      }}
                    >
                      Reject
                    </Button>
                    <Button type="button" variant="ghost" size="sm" disabled={busyId === item.id} onClick={() => void decide(item, 'needs_more_info')}>
                      Needs info
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : tab === 'health' ? (
        <div className="form-section max-w-2xl space-y-3">
          <p className="text-sm text-gray-300">
            Pending review: <span className="text-white tabular-nums">{String(health?.pending_review_count ?? '—')}</span>
          </p>
          <p className="text-sm text-gray-300">
            Held risk: <span className="text-white tabular-nums">{String(health?.held_risk_count ?? '—')}</span>
          </p>
          <p className="text-xs text-gray-500">
            Use CLI <code className="font-mono">php backend/tools/verify_passport_health.php</code> for table/file checks and{' '}
            <code className="font-mono">repair_passport_aggregates.php --dry-run</code> before repairs. Platform analytics includes a Passport
            section when the admin analytics bundle is loaded.
          </p>
          {health?.tables && typeof health.tables === 'object' ? (
            <ul className="mt-3 space-y-1 text-xs font-mono text-zinc-400">
              {Object.entries(health.tables as Record<string, boolean>).map(([table, ok]) => (
                <li key={table}>
                  {ok ? '✓' : '✗'} {table}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : encounters.length === 0 ? (
        <AdminQueueState variant="empty" title="No encounters found" description="Try a different status filter." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="text-gray-400 border-b border-white/[0.06]">
                <th className="py-2 pr-4">Subject</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Verification</th>
                <th className="py-2 pr-4">Risk</th>
                <th className="py-2 pr-4">Logged</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {encounters.map((enc) => (
                <tr key={enc.public_id} className="border-b border-white/[0.06]">
                  <td className="py-3 pr-4">
                    <Link to={`/passport/encounters/${encodeURIComponent(enc.public_id)}`} className="text-orange-300 hover:underline font-medium">
                      {encounterSubjectLabel(enc)}
                    </Link>
                  </td>
                  <td className="py-3 pr-4 text-gray-300">{encounterStatusLabel(enc.status)}</td>
                  <td className="py-3 pr-4 text-gray-300">{verificationLevelLabel(enc.verification_level)}</td>
                  <td className="py-3 pr-4 text-gray-300">{riskStatusLabel(enc.risk_status)}</td>
                  <td className="py-3 pr-4 text-gray-500">{formatEncounterDateTime(enc.occurred_at)}</td>
                  <td className="py-3 pr-4">
                    {enc.status !== 'rejected' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busyId === enc.id}
                        onClick={() => {
                          setRejectTarget(enc);
                          setRejectReason('');
                        }}
                      >
                        Reject
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(rejectTarget)}
        title="Mark this encounter as not verified?"
        description="The member will see this as not verified and can appeal from the encounter detail page."
        confirmLabel="Reject"
        cancelLabel="Cancel"
        destructive
        loading={busyId !== null}
        onCancel={() => setRejectTarget(null)}
        onConfirm={() => void confirmReject()}
      >
        <textarea
          className="input input-textarea w-full mt-3"
          placeholder="Optional reason (kept for the audit log)"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          aria-label="Rejection reason"
        />
      </ConfirmDialog>
    </div>
  );
};

export default AdminPassport;
